import { createApi } from '@reduxjs/toolkit/query/react';

import baseQuery from 'src/utils/baseQuery';

export type WorkflowType = 'RULE' | 'AI' | 'HYBRID';
export type WorkflowStatus = 'ACTIVE' | 'DRAFT' | 'INACTIVE';

export interface WorkflowNode {
  id: string;
  type:
    | 'START'
    | 'END'
    | 'TRIGGER'
    | 'ACTION'
    | 'CONDITION'
    | 'DELAY'
    | 'HUMAN_INPUT'
    | 'SWITCH'
    | 'AI_TASK'
    | 'LOOP'
    | 'SPLIT'
    | 'MERGE'
    | 'SUB_WORKFLOW'
    | 'BUSINESS_HOURS'
    | 'NOTE';
  label?: string;
  actionType?: string;
  parameters?: Record<string, any>;
  next?: string;
  nextTrue?: string;
  nextFalse?: string;
  onTrue?: string;
  onFalse?: string;
  branches?: string[];
  cases?: Record<string, string>;
  default?: string;
  task?: string;
  model?: string;
  confidenceThreshold?: number;
  triggerType?: string;
  delayMs?: number;
  condition?: any;
  position?: { x: number; y: number };
  data?: Record<string, any>;
}

export interface WorkflowEdge {
  id: string;
  source: string;
  target: string;
  sourceHandle?: string;
  targetHandle?: string;
  type?: string;
}

export interface Workflow {
  id: string;
  tenantId?: string;
  name: string;
  description?: string;
  version?: string;
  type: WorkflowType;
  status: WorkflowStatus;
  trigger?: Record<string, any>;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  variables?: Record<string, any>;
  lastRunAt?: string;
  createdAt: string;
  updatedAt: string;
  _count?: {
    executions: number;
  };
}

export interface WorkflowListParams {
  page?: number;
  limit?: number;
  filters?: any[];
  globalSearch?: string;
}

export interface WorkflowListResponse {
  list: Workflow[];
  total: number;
  totalPages: number;
}

export interface CreateWorkflowBody {
  name: string;
  description?: string;
  version?: string;
  type: WorkflowType;
  status?: WorkflowStatus;
  nodes?: WorkflowNode[];
  edges?: WorkflowEdge[];
}
export interface UpdateWorkflowBody {
  id: string;
  name?: string;
  description?: string;
  version?: string;
  type?: WorkflowType;
  status?: WorkflowStatus;
  nodes?: WorkflowNode[];
  edges?: WorkflowEdge[];
}
export interface ExecuteWorkflowBody {
  id: string;
  context?: Record<string, any>;
}

export interface GenerateWorkflowBody {
  prompt: string;
  type?: WorkflowType;
}

export interface NodeExecution {
  nodeId: string;
  nodeType: string;
  status: 'PENDING' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'SKIPPED';
  input?: Record<string, any>;
  output?: Record<string, any>;
  error?: string;
  startedAt: string;
  completedAt?: string;
  retries?: number;
}

export interface WorkflowExecution {
  id: string;
  workflowId: string;
  status: 'RUNNING' | 'COMPLETED' | 'FAILED' | 'PAUSED' | 'CANCELLED';
  startedAt: string;
  completedAt?: string;
  currentNodeId?: string;
  error?: string;
  context?: Record<string, any>;
  executionHistory?: NodeExecution[];
  nodeOutputs?: Record<string, Record<string, any>>;
  duration?: number;
  triggerType?: string;
}

export interface WorkflowExecutionListParams {
  workflowId: string;
  page?: number;
  limit?: number;
}

export interface WorkflowTemplate {
  id: string;
  name: string;
  description?: string;
  type: WorkflowType;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
}

/** JSON Schema shape returned by GET /api/workflows/nodes/metadata */
export interface NodeMetadata {
  type: string;
  category: 'LOGIC' | 'ACTION' | 'AI' | 'FLOW';
  label: string;
  description: string;
  icon: string;
  inputSchema: Record<string, any>;
  outputSchema?: Record<string, any>;
  uiConfig?: { color?: string; minWidth?: number };
}

export interface DryRunNodeBody {
  nodeId: string;
  workflowId: string;
  /** Full node definition — required by backend */
  node: WorkflowNode;
  /** ResolverContext passed to the engine */
  context: {
    trigger: Record<string, any>;
    nodes: Record<string, Record<string, any>>;
    now?: string;
    tenantId?: string;
    executionId?: string;
  };
}

export interface DryRunNodeResult {
  resolvedInputs: Record<string, any>;
  output: { dryRun: true; wouldHaveSent: Record<string, any> } | Record<string, any>;
  error?: string;
}

export interface TestRunWorkflowBody {
  id: string;
  triggerPayload?: Record<string, any>;
}

export interface TestRunWorkflowResult {
  triggerPayload: Record<string, any>;
  nodeOutputs: Record<string, Record<string, any>>;
  executionHistory: NodeExecution[];
  status: string;
}

export interface WorkflowAnalytics {
  /** Keyed by "sourceNodeId:targetNodeId" — matches edge traversal counts */
  edgeCounts: Record<string, number>;
  /** Keyed by nodeId — how many times each node ran */
  nodeCounts: Record<string, number>;
  totalExecutions: number;
  completedExecutions: number;
  failedExecutions: number;
  averageDurationMs: number;
}

export interface SuggestConditionBody {
  contextFields: string[];
  description?: string;
  currentField?: string;
}

export interface SuggestConditionResult {
  field: string;
  operator: string;
  value: string;
  explanation: string;
}

export interface WorkflowVersion {
  id: string;
  workflowId: string;
  version: number;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  variables?: Record<string, any>;
  createdBy?: string;
  createdAt: string;
}

export interface WorkflowUserTemplate {
  id: string;
  name: string;
  description?: string;
  category: string;
  type: WorkflowType;
  nodes: WorkflowNode[];
  edges: WorkflowEdge[];
  variables?: Record<string, any>;
  createdBy?: string;
  createdAt: string;
}

export interface CreateUserTemplateBody {
  workflowId: string;
  name: string;
  description?: string;
  category?: string;
}

export interface WorkflowVariable {
  key: string;
  label: string;
  displayPath: string;
  expression: string;
  type: 'string' | 'number' | 'boolean' | 'object' | 'array';
  category: 'TRIGGER' | 'SYSTEM';
  description?: string;
  example?: string;
  children?: WorkflowVariable[];
}

export interface VariableTreeResponse {
  trigger: WorkflowVariable[];
  system: WorkflowVariable[];
  nodes: Array<{
    nodeId: string;
    nodeLabel: string;
    nodeType: string;
    actionType?: string;
    variables: WorkflowVariable[];
  }>;
  example?: any;
}

export interface WebhookVariable {
  key: string;     // e.g. "trigger.payload.email"
  label: string;   // e.g. "Contact Email"
  example?: string;
}

export interface WebhookConfig {
  url: string;
  secret?: string;
  enabled: boolean;
  /** Legacy: single example body (deprecated) */
  exampleBody?: Record<string, any>;
  /** Per-module example bodies: contact / account / ticket */
  exampleBodies?: Record<string, Record<string, any>>;
  variables?: WebhookVariable[];
}

export const workflowApi = createApi({
  reducerPath: 'workflowApi',
  baseQuery,
  tagTypes: ['Workflow'],
  endpoints: (builder) => ({
    listWorkflows: builder.query<WorkflowListResponse, WorkflowListParams>({
      query: (body) => ({
        url: 'api/workflows/list',
        method: 'POST',
        body: {
          page: body.page ?? 1,
          limit: body.limit ?? 20,
          filters: body.filters ?? [],
          globalSearch: body.globalSearch ?? '',
        },
      }),
      transformResponse: (response: { data?: any }) => {
        const list = Array.isArray(response?.data) ? response.data : (response?.data?.list ?? []);
        return {
          list,
          total: response?.data?.total ?? list.length,
          totalPages: response?.data?.totalPages ?? 1,
        };
      },
      providesTags: (result) =>
        result
          ? [...result.list.map(({ id }) => ({ type: 'Workflow' as const, id })), 'Workflow']
          : ['Workflow'],
    }),
    getWorkflow: builder.query<Workflow, string>({
      query: (id) => ({
        url: `api/workflows/${id}`,
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
      providesTags: (_result, _error, id) => [{ type: 'Workflow', id }],
      // Unsubscribe immediately when the builder unmounts so this query
      // doesn't sit in the cache for 60 s and re-fetch when list screen loads.
      keepUnusedDataFor: 0,
    }),
    createWorkflow: builder.mutation<Workflow, CreateWorkflowBody>({
      query: (body) => ({
        url: 'api/workflows',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
      invalidatesTags: ['Workflow'],
    }),
    updateWorkflow: builder.mutation<Workflow, UpdateWorkflowBody>({
      query: ({ id, ...body }) => ({
        url: `api/workflows/${id}`,
        method: 'PUT',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
      // Only invalidate the specific entity — not the whole 'Workflow' list tag.
      // Invalidating both causes RTK Query to schedule two re-fetches of getWorkflow
      // which manifests as a second API call in the network tab.
      invalidatesTags: (_result, _error, { id }) => [{ type: 'Workflow', id }],
    }),
    deleteWorkflow: builder.mutation<void, string>({
      query: (id) => ({
        url: `api/workflows/${id}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Workflow'],
    }),
    executeWorkflow: builder.mutation<any, ExecuteWorkflowBody>({
      query: ({ id, context }) => ({
        url: `api/workflows/${id}/execute`,
        method: 'POST',
        body: { context },
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
    }),
    resumeExecution: builder.mutation<
      WorkflowExecution,
      { executionId: string; input?: Record<string, any> }
    >({
      query: ({ executionId, input }) => ({
        url: `api/workflows/executions/${executionId}/resume`,
        method: 'POST',
        body: { input },
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
    }),
    publishWorkflow: builder.mutation<Workflow, string>({
      query: (id) => ({
        url: `api/workflows/${id}/publish`,
        method: 'POST',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
      invalidatesTags: (_result, _error, id) => [{ type: 'Workflow', id }, 'Workflow'],
    }),
    listExecutions: builder.query<
      { list: WorkflowExecution[]; total: number },
      WorkflowExecutionListParams
    >({
      query: ({ workflowId, page = 1, limit = 20 }) => ({
        url: `api/workflows/${workflowId}/executions?page=${page}&limit=${limit}`,
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) => ({
        list: response?.data?.list ?? response?.data ?? [],
        total: response?.data?.total ?? 0,
      }),
    }),
    getExecution: builder.query<WorkflowExecution, { workflowId: string; executionId: string }>({
      query: ({ workflowId, executionId }) => ({
        url: `api/workflows/${workflowId}/executions/${executionId}`,
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
    }),
    generateWorkflow: builder.mutation<Workflow, GenerateWorkflowBody>({
      query: (body) => ({
        url: 'api/workflows/generate',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
    }),
    getWorkflowTemplates: builder.query<WorkflowTemplate[], void>({
      query: () => ({
        url: 'api/workflows/templates',
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? [],
    }),

    // Node metadata — fetched once at app init, cached indefinitely (never changes at runtime)
    getNodesMetadata: builder.query<NodeMetadata[], void>({
      query: () => ({
        url: 'api/workflows/nodes/metadata',
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? [],
      keepUnusedDataFor: Infinity,
    }),

    // Dry-run a single node — fixes existing /nodes/dry-run endpoint
    dryRunNode: builder.mutation<DryRunNodeResult, DryRunNodeBody>({
      query: (body) => ({
        url: 'api/workflows/nodes/dry-run',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
    }),

    // Synchronous test-run: runs the full workflow dry (side effects skipped, FETCH actions real)
    testRunWorkflow: builder.mutation<TestRunWorkflowResult, TestRunWorkflowBody>({
      query: ({ id, triggerPayload }) => ({
        url: `api/workflows/${id}/test-run`,
        method: 'POST',
        body: { triggerPayload: triggerPayload ?? {} },
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
    }),

    // ── Entity picker queries ─────────────────────────────────────────────────
    // Used by EntityPickerField to fetch entity lists for autocomplete.
    // Defined as queries (not mutations) so RTK Query deduplicates across multiple
    // EntityPickerField instances on the same canvas.

    getEntityTeams: builder.query<Array<{ id: string; name: string }>, void>({
      query: () => ({
        url: 'api/teams/list',
        method: 'POST',
        body: { filters: [], page: 1, limit: 100 },
      }),
      transformResponse: (response: { data?: { list?: any[] } }) =>
        (response?.data?.list ?? []).map((t: any) => ({
          id: t.id ?? t._id ?? '',
          name: t.name ?? t.teamName ?? '',
        })),
    }),

    getEntityUsers: builder.query<
      Array<{ id: string; name: string; role?: string }>,
      { role?: string } | void
    >({
      query: (args) => ({
        url: 'api/users/list',
        method: 'POST',
        body: {
          filters: [
            { fieldname: 'status', search: 'ACTIVE' },
            { fieldname: 'createdAt', sort: 1 },
            ...(args?.role ? [{ fieldname: 'role', search: args.role }] : []),
          ],
          globalSearch: '',
          page: 1,
          limit: 100,
        },
      }),
      transformResponse: (response: { data?: { list?: any[] } | any[] }) => {
        const list = Array.isArray(response?.data) ? response.data : (response?.data?.list ?? []);
        return list.map((u: any) => ({
          id: u.id ?? u._id ?? '',
          name: `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim() || u.email || u.id,
          role: u.role,
        }));
      },
    }),

    getConfiguredChannels: builder.query<
      Array<{ id: string; name: string; channelType: string }>,
      void
    >({
      query: () => ({
        url: 'api/channels/list',
        method: 'POST',
        body: { filters: [{ fieldname: 'createdAt', sort: 1 }], page: 1, limit: 100 },
      }),
      transformResponse: (response: { data?: { list?: any[] } }) =>
        (response?.data?.list ?? []).map((c: any) => ({
          id: c.id ?? c._id ?? '',
          name: c.channelName ?? c.name ?? c.channelType ?? '',
          channelType: c.channelType ?? '',
        })),
    }),

    getWhatsappTemplates: builder.query<any[], string>({
      query: (channelId) => ({
        url: `api/channels/${channelId}/whatsapp-templates`,
        method: 'GET',
      }),
      transformResponse: (response: { data?: any[] }) => response?.data ?? [],
    }),

    // ── Contact / Ticket / Account entity search ──────────────────────────────
    // Used by EntityPickerField for contact/ticket/account pickers.
    // Accept an optional search string; backend returns paginated results.

    getEntityContacts: builder.query<
      Array<{ id: string; name: string; email?: string }>,
      { search?: string } | void
    >({
      query: (args) => ({
        url: 'api/contacts/list',
        method: 'POST',
        body: { search: args?.search ?? '', page: 1, limit: 50 },
      }),
      transformResponse: (response: { data?: { list?: any[] } }) =>
        (response?.data?.list ?? []).map((c: any) => ({
          id: c.id ?? c._id ?? '',
          name: `${c.firstName ?? ''} ${c.lastName ?? ''}`.trim() || c.email || c.id,
          email: c.email,
        })),
      keepUnusedDataFor: 30,
    }),

    getEntityTickets: builder.query<
      Array<{ id: string; name: string; status?: string }>,
      { search?: string } | void
    >({
      query: (args) => ({
        url: 'api/tickets/list',
        method: 'POST',
        body: { search: args?.search ?? '', page: 1, limit: 50 },
      }),
      transformResponse: (response: { data?: { list?: any[] } }) =>
        (response?.data?.list ?? []).map((t: any) => ({
          id: t.id ?? t._id ?? '',
          name: t.title ?? t.subject ?? t.id,
          status: t.status,
        })),
      keepUnusedDataFor: 30,
    }),

    getEntityAccounts: builder.query<
      Array<{ id: string; name: string }>,
      { search?: string } | void
    >({
      query: (args) => ({
        url: 'api/companies/list',
        method: 'POST',
        body: { search: args?.search ?? '', page: 1, limit: 50 },
      }),
      transformResponse: (response: { data?: { list?: any[] } }) =>
        (response?.data?.list ?? []).map((a: any) => ({
          id: a.id ?? a._id ?? '',
          name: a.name ?? a.companyName ?? a.id,
        })),
      keepUnusedDataFor: 30,
    }),

    // ── Analytics + AI ────────────────────────────────────────────────────────

    getWorkflowAnalytics: builder.query<WorkflowAnalytics, string>({
      query: (id) => ({
        url: `api/workflows/${id}/analytics`,
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
      keepUnusedDataFor: 60,
    }),

    getTriggerSampleData: builder.query<Record<string, any>, string>({
      query: (triggerType) => ({
        url: `api/workflows/trigger-sample-data?type=${encodeURIComponent(triggerType)}`,
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? {},
      keepUnusedDataFor: 300,
    }),

    suggestCondition: builder.mutation<SuggestConditionResult, SuggestConditionBody>({
      query: (body) => ({
        url: 'api/workflows/suggest-condition',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
    }),

    // ── Version history ───────────────────────────────────────────────────────

    listWorkflowVersions: builder.query<WorkflowVersion[], string>({
      query: (workflowId) => ({
        url: `api/workflows/${workflowId}/versions`,
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) =>
        Array.isArray(response?.data) ? response.data : [],
      keepUnusedDataFor: 30,
    }),

    createVersionSnapshot: builder.mutation<WorkflowVersion, string>({
      query: (workflowId) => ({
        url: `api/workflows/${workflowId}/versions`,
        method: 'POST',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
      // Invalidate the version list after snapshot
      invalidatesTags: (_result, _error, workflowId) => [{ type: 'Workflow', id: workflowId }],
    }),

    restoreVersion: builder.mutation<Workflow, { workflowId: string; versionId: string }>({
      query: ({ workflowId, versionId }) => ({
        url: `api/workflows/${workflowId}/versions/${versionId}/restore`,
        method: 'POST',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
      invalidatesTags: (_result, _error, { workflowId }) => [{ type: 'Workflow', id: workflowId }],
    }),

    // ── User templates ────────────────────────────────────────────────────────

    listUserTemplates: builder.query<WorkflowUserTemplate[], void>({
      query: () => ({
        url: 'api/workflows/user-templates',
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) =>
        Array.isArray(response?.data) ? response.data : [],
      providesTags: ['Workflow'],
    }),

    createUserTemplate: builder.mutation<WorkflowUserTemplate, CreateUserTemplateBody>({
      query: (body) => ({
        url: 'api/workflows/user-templates',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
      invalidatesTags: ['Workflow'],
    }),

    deleteUserTemplate: builder.mutation<void, string>({
      query: (templateId) => ({
        url: `api/workflows/user-templates/${templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Workflow'],
    }),
    getEntityLabels: builder.query<Array<{ id: string; name: string }>, void>({
      query: () => ({
        url: 'api/labels/list',
        method: 'POST',
        body: { filters: [], page: 1, limit: 1000 },
      }),
      transformResponse: (response: { data?: { list?: any[] } | any[] }) => {
        const list = Array.isArray(response?.data) ? response.data : (response?.data?.list ?? []);
        return list.map((l: any) => ({
          id: l.labelName ?? l.id ?? l._id ?? '',
          name: l.labelName ?? 'Unknown Label',
        }));
      },
    }),

    // ── Dynamic Variables API ────────────────────────────────────────────────
    listTriggerTypes: builder.query<string[], void>({
      query: () => ({
        url: 'api/workflows/variables/trigger-types',
        method: 'GET',
      }),
      transformResponse: (response: { data?: string[] }) => response?.data ?? [],
    }),

    getInitialVariables: builder.query<
      VariableTreeResponse,
      { triggerType?: string; workflowId?: string } | void
    >({
      query: (params) => {
        const queryParams = new URLSearchParams();
        if (params?.triggerType) queryParams.append('triggerType', params.triggerType);
        if (params?.workflowId) queryParams.append('workflowId', params.workflowId);

        const queryString = queryParams.toString();
        return {
          url: `api/workflows/variables${queryString ? `?${queryString}` : ''}`,
          method: 'GET',
        };
      },
      transformResponse: (response: { data?: VariableTreeResponse }) =>
        response?.data ?? { trigger: [], system: [], nodes: [] },
    }),

    getFullVariableTree: builder.query<
      VariableTreeResponse,
      { triggerType?: string; workflowId?: string | null; nodes: any[]; edges: any[] }
    >({
      query: (body) => ({
        url: 'api/workflows/variables',
        method: 'POST',
        body,
        headers: { 'Content-Type': 'application/json' },
      }),
      transformResponse: (response: { data?: VariableTreeResponse }) =>
        response?.data ?? { trigger: [], system: [], nodes: [] },
    }),

    getNodeOutputVariables: builder.query<
      WorkflowVariable[],
      { nodeType: string; actionType?: string; nodeId?: string; nodeLabel?: string }
    >({
      query: ({ nodeType, actionType, nodeId, nodeLabel }) => {
        const params = new URLSearchParams();
        params.append('nodeType', nodeType);
        if (actionType) params.append('actionType', actionType);
        if (nodeId) params.append('nodeId', nodeId);
        if (nodeLabel) params.append('nodeLabel', nodeLabel);
        return {
          url: `api/workflows/variables/node-outputs?${params.toString()}`,
          method: 'GET',
        };
      },
      transformResponse: (response: { data?: WorkflowVariable[] }) => response?.data ?? [],
    }),

    // ── Webhook config ────────────────────────────────────────────────────────
    // Fetches the webhook URL, secret, example body and available variables
    // for a workflow whose trigger type is WEBHOOK.
    getWebhookConfig: builder.query<WebhookConfig, string>({
      query: (workflowId) => ({
        url: `api/workflows/${workflowId}/trigger-config`,
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
      // Never cache stale — always refetch so secret rotations are reflected.
      keepUnusedDataFor: 0,
    }),

    // Rotates the HMAC signing secret for the webhook URL.
    regenerateWebhookSecret: builder.mutation<WebhookConfig, string>({
      query: (workflowId) => ({
        url: `api/workflows/${workflowId}/trigger-config/regenerate-secret`,
        method: 'POST',
      }),
      transformResponse: (response: { data?: any }) => response?.data ?? null,
    }),

    // ── Webhook specific ───────────────────────────────────────────────────
    getWebhookSchema: builder.query<VariableTreeResponse, string>({
      query: (workflowId) => ({
        url: `api/workflows/${workflowId}/workflow-schema`,
        method: 'GET',
      }),
      transformResponse: (response: { data?: any }) => {
        const schema = response?.data?.schema;
        if (!schema || !schema.children) {
          return { trigger: [], system: [], nodes: [] };
        }

        const transform = (key: string, node: any, parentPath: string = ''): any => {
          const displayPath = parentPath ? `${parentPath}.${key}` : key;
          return {
            key,
            label: key,
            type: node.type || 'string',
            displayPath,
            expression: `{{${displayPath}}}`,
            category: 'TRIGGER',
            children: node.children
              ? Object.entries(node.children).map(([k, v]) => transform(k, v, displayPath))
              : undefined,
          };
        };

        // The backend schema represents the trigger payload
        const triggerVars = Object.entries(schema.children).map(([k, v]) => transform(k, v, ''));

        return {
          trigger: triggerVars,
          system: [],
          nodes: [],
          example: schema.example || null,
        };
      },
    }),
  }),
});

export const {
  useListWorkflowsQuery,
  useGetWorkflowQuery,
  useCreateWorkflowMutation,
  useUpdateWorkflowMutation,
  useDeleteWorkflowMutation,
  useExecuteWorkflowMutation,
  useResumeExecutionMutation,
  usePublishWorkflowMutation,
  useListExecutionsQuery,
  useGetExecutionQuery,
  useGenerateWorkflowMutation,
  useGetWorkflowTemplatesQuery,
  useGetNodesMetadataQuery,
  useDryRunNodeMutation,
  useTestRunWorkflowMutation,
  useGetEntityTeamsQuery,
  useGetEntityUsersQuery,
  useGetConfiguredChannelsQuery,
  useGetWhatsappTemplatesQuery,
  useGetEntityContactsQuery,
  useGetEntityTicketsQuery,
  useGetEntityAccountsQuery,
  useGetWorkflowAnalyticsQuery,
  useGetTriggerSampleDataQuery,
  useSuggestConditionMutation,
  useListWorkflowVersionsQuery,
  useCreateVersionSnapshotMutation,
  useRestoreVersionMutation,
  useListUserTemplatesQuery,
  useCreateUserTemplateMutation,
  useDeleteUserTemplateMutation,
  useGetEntityLabelsQuery,
  useListTriggerTypesQuery,
  useGetInitialVariablesQuery,
  useGetFullVariableTreeQuery,
  useGetNodeOutputVariablesQuery,
  useGetWebhookConfigQuery,
  useRegenerateWebhookSecretMutation,
  useGetWebhookSchemaQuery,
} = workflowApi;


